import { Method } from 'axios';
import {
  GeoLocation,
  WalletPass,
  WalletPassData,
  WalletPassInput,
  WalletPerson,
} from './interfaces';
import { Executable } from './executable';

/**
 * Updates an existing wallet pass either by the pass identifier or the email address used on the pass and the program identifier.
 */
export class UpdateWallet extends Executable<WalletPass> implements Partial<WalletPassInput> {
  /**
   * The brand identifier.
   */
  public brandId?: number;

  /**
   * The program identifier.
   */
  public programId?: number;

  /**
   * The pass identifier. Optional if either email + programId or uniqueId + programId are provided
   */
  public passId?: number;

  /**
   * The email address used on the pass. Optional if either passId or uniqueId are provided.
   */
  public email?: string;

  /**
   * The unique identifier used on the pass. Optional if either passId or email are provided.
   */
  public uniqueId?: string;

  public passdata?: WalletPassData;

  /**
   * The person record.
   */
  public person?: WalletPerson;

  /**
   * The current message that is displayed on the wallet pass.
   */
  public currentMessage?: string;

  /**
   * The tier to put the card in during creation, if not provided will use the default tier.
   */
  public templateTierId?: number;

  /**
   * The location from which the card is being issued.
   */
  public geoLocation?: GeoLocation;

  /**
   * The ipaddress which is requesting the card to be issued.
   */
  public ipAddress?: string;

  /**
   * The templateId from which the card is being issued.  If a program is provided, this is not required.
   */
  public templateId?: number;

  public dynamicImageUrl?: string;

  public getMethod(): Method {
    return 'patch';
  }

  public getResponse(apiResponse: any): WalletPass {
    return apiResponse;
  }

  public getURLPath(tenantId?: string): string {
    if (!this.brandId) throw new Error('brandId is required');

    if (this.passId) {
      return `${this.brandId}/wallets/${this.passId}`;
    }

    if (this.email && this.programId) {
      return `${this.brandId}/programs/${this.programId}/wallets?email=${encodeURIComponent(
        this.email
      )}`;
    }

    if (this.uniqueId && this.programId) {
      return `${this.brandId}/programs/${this.programId}/wallets?unique_id=${encodeURIComponent(
        this.uniqueId
      )}`;
    }

    throw new Error('passId or email and programId are required');
  }

  constructor(input?: Partial<WalletPassInput> & { brandId: number }) {
    super();
    if (input) {
      Object.assign(this, input);
    }
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): Partial<WalletPassInput> {
    return {
      person: this.person,
      passdata: this.passdata,
      templateId: this.templateId,
      templateTierId: this.templateTierId,
      currentMessage: this.currentMessage,
      geoLocation: this.geoLocation,
      ipAddress: this.ipAddress,
      dynamicImageUrl: this.dynamicImageUrl,
    };
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return 'brands';
  }
}
